import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import Description from 'Components/Blocks/Description'
import { Column, Text } from 'Components/UI'
import truncate from 'truncate-html'

import { useResponsiveLayout } from 'Hooks'

const DEFAULT_MAX_HEIGHT = 150

interface PostPreviewProps {
  title: string
  description?: string
  maxHeight?: number
  ellipsis?: string
}

function PostPreview({
  title,
  description = '',
  maxHeight = DEFAULT_MAX_HEIGHT,
  ellipsis = ' […]',
}: PostPreviewProps) {
  const { isDesktop } = useResponsiveLayout()

  const [clampedHtml, setClampedHtml] = useState<string | undefined>(undefined)
  const [measuredTitleHeight, setMeasuredTitleHeight] = useState(0)

  const titleRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const truncateMemo = useMemo(() => new Map<number, string>(), [])

  const getTruncated = useCallback(
    (length: number) => {
      if (!truncateMemo.has(length)) {
        truncateMemo.set(length, truncate(description, length, { ellipsis }))
      }
      return truncateMemo.get(length)!
    },
    [description, ellipsis, truncateMemo],
  )

  const createHiddenDiv = (width: number, height: number) => {
    const temp = document.createElement('div')
    temp.style.position = 'absolute'
    temp.style.visibility = 'hidden'
    temp.style.pointerEvents = 'none'
    temp.style.width = `${width}px`
    temp.style.maxHeight = `${height}px`
    temp.style.overflow = 'hidden'
    document.body.appendChild(temp)
    return temp
  }

  const updateTruncation = useCallback(() => {
    if (!containerRef.current || !description) {
      setClampedHtml(description)
      return
    }

    const availableHeight = maxHeight - measuredTitleHeight
    if (availableHeight <= 0) {
      setClampedHtml('')
      return
    }

    const containerWidth = containerRef.current.offsetWidth
    const tempDiv = createHiddenDiv(containerWidth, availableHeight)

    let low = 0
    let high = description.length
    let bestFitHtml = description

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      const truncated = getTruncated(mid)

      tempDiv.innerHTML = truncated
      const fits = tempDiv.scrollHeight <= availableHeight

      if (fits) {
        bestFitHtml = truncated
        low = mid + 1
      } else {
        high = mid - 1
      }
    }

    setClampedHtml(bestFitHtml)
    document.body.removeChild(tempDiv)
  }, [description, measuredTitleHeight, maxHeight, getTruncated])

  useLayoutEffect(() => {
    if (titleRef.current) {
      setMeasuredTitleHeight(titleRef.current.offsetHeight)
    }
  }, [title])

  useLayoutEffect(() => {
    const handle = requestAnimationFrame(updateTruncation)

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateTruncation)
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      cancelAnimationFrame(handle)
      resizeObserver.disconnect()
    }
  }, [updateTruncation])

  return (
    <div ref={containerRef}>
      <Column maxHeight={maxHeight} overflowY="hidden">
        <div ref={titleRef}>
          <Text
            bold
            important
            left
            mb={4}
            screen14={!isDesktop}
            screen18={isDesktop}
            width={1}
          >
            {title}
          </Text>
        </div>

        {clampedHtml && <Description value={description} />}
      </Column>
    </div>
  )
}

export default PostPreview
